/**
 * Director (Velo) metrics calculator
 * Calculates all metrics specific to the director/management nodes
 */

import { calculateCapacity } from '../shared/capacityHelpers';
import { PART_NUMBERS } from '../../utils/partNumbers';
import { getProductPower, getProductWeight, getProductMaxPower } from '../../utils/configUtilities';
import type { DirectorConfig } from '../../domain';
import type { MetricsContext, ProductDb, MetricsDb } from '../types';

/**
 * Calculate all director-specific metrics
 * @param director - Director box from SystemConfiguration
 * @param ctx - Cross-box inputs (rack heights)
 * @param productDb - Product database
 * @param metricsDb - Metrics database with performance data
 * @returns Director metrics (flat, same keys as before)
 */
export default function calculateDirectorMetrics(
  director: DirectorConfig,
  ctx: MetricsContext,
  productDb: ProductDb,
  metricsDb: MetricsDb,
) {
    const { veloCount, veloSsdCapacity } = director;
    const ssdPerVelo = metricsDb.global.ssdPerVelo;

    // ========== CAPACITY CALCULATIONS ==========
    const totalVeloSsdCapacity = calculateCapacity(veloCount, ssdPerVelo, veloSsdCapacity);

    // ========== PERFORMANCE CALCULATIONS ==========
    // MORK TODO : Global system metric?
    const totalMetadata = veloCount * metricsDb.global.metadataPerVelo;

    // ========== PHYSICAL CALCULATIONS ==========
    const veloDirectorPartNumber = PART_NUMBERS.NODES.DIRECTOR;

    const veloNominalPower = veloCount * getProductPower(productDb, veloDirectorPartNumber);
    const veloMaxPower = veloCount * getProductMaxPower(productDb, veloDirectorPartNumber);
    const veloWeight = veloCount * getProductWeight(productDb, veloDirectorPartNumber);
    const veloRU = veloCount * ctx.rackHeights.director;

    // ========== RETURN ALL METRICS ==========
    return {
        // Capacity
        totalVeloSsdCapacity,

        // Performance
        totalMetadata,

        // Physical
        veloNominalPower,
        veloMaxPower,
        veloWeight,
        veloRU
    };
}
